import moment from "moment";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import Avatar from "../../../../parts/avatar";
import DropdownOptions from "../../../../parts/dropdownOptions";
import SvgIcon from "../../../../parts/SvgIcon";
import { useRillContext } from "../../../../rill/RillReact/share/base";
import getAvatarUrl from "../../../../utils/get-avatar-url";
import { isPullActiveCoach } from "../../../../utils/user-helpers";
import EditEmailModal from "./EditEmailModal";

const AccessRequestsTable = ({
  clients,
  sortConfig,
  handleSort,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
}) => {
  const { t } = useTranslation("coaching-portal");
  const [editingClient, setEditingClient] = useState(null);

  return (
    <section role="table-wrapper">
      <table role="report-table" className="access-requests-table">
        <thead role="table-header">
          <tr>
            <th className="checkbox-col">
              <input
                type="checkbox"
                checked={
                  clients.length > 0 && selectedIds.size === clients.length
                }
                onChange={onToggleSelectAll}
              />
            </th>
            <th>
              {t("accessRequests.memberColumn")}{" "}
              <SvgIcon
                src="/img/svg/up_down_arrows.svg"
                onClickFn={() =>
                  handleSort(
                    { target: { value: "name" } },
                    sortConfig.key === "name" &&
                      sortConfig.direction !== "descending"
                      ? "descending"
                      : "ascending"
                  )
                }
              />
            </th>
            <th>
              {t("accessRequests.requestDateColumn")}{" "}
              <SvgIcon
                src="/img/svg/up_down_arrows.svg"
                onClickFn={() =>
                  handleSort(
                    { target: { value: "addedAt" } },
                    sortConfig.key === "addedAt" &&
                      sortConfig.direction !== "descending"
                      ? "descending"
                      : "ascending"
                  )
                }
              />
            </th>
            <th>{t("accessRequests.actionColumn")}</th>
          </tr>
        </thead>
        {clients.length > 0 ? (
          <tbody>
            {clients.map((client) => (
              <AccessRequestRow
                key={client._id}
                client={client}
                isSelected={selectedIds.has(client._id)}
                onToggleSelect={onToggleSelect}
                onEditEmail={() => setEditingClient(client)}
              />
            ))}
          </tbody>
        ) : (
          <EmptyState />
        )}
      </table>
      {editingClient && (
        <EditEmailModal
          client={editingClient}
          onClose={(e) => {
            e?.preventDefault?.();
            setEditingClient(null);
          }}
        />
      )}
    </section>
  );
};

const AccessRequestRow = ({
  client,
  isSelected,
  onToggleSelect,
  onEditEmail,
}) => {
  const { t } = useTranslation("coaching-portal");
  const [menuOpen, setMenuOpen] = useState(false);
  const { locals } = useRillContext();
  const { activeUserToRequest } = locals;
  const isActivePullCoach = isPullActiveCoach(activeUserToRequest);

  const dropdownOptions = [
    { label: t("accessRequests.pullClientOption"), value: "pull" },
  ];

  const handleAccept = () => {
    submit("/coaching-portal/access-requests/approve", {
      method: "POST",
      body: {
        clientId: client._id,
        type: "perform",
      },
    });
  };

  const handleDropdownSelect = (value) => {
    if (value === "pull") {
      submit("/coaching-portal/access-requests/approve", {
        method: "POST",
        body: {
          clientId: client._id,
          type: "pull",
        },
      });
    }
  };

  const handleReject = () => {
    if (!confirm(t("accessRequests.rejectConfirm"))) {
      return;
    }
    submit("/coaching-portal/access-requests/reject", {
      method: "POST",
      body: {
        clientId: client._id,
      },
    });
  };

  return (
    <tr>
      <td className="checkbox-col">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => onToggleSelect(client._id)}
        />
      </td>
      <td>
        <div className="athlete-cell">
          <div className="image-wrapper">
            {client.avatar ? (
              <Avatar size="46" url={getAvatarUrl(client)} user={{}} />
            ) : (
              <Avatar size="46" user={{ name: client.name }} />
            )}
          </div>
          <div className="athlete-info">
            <span className="athlete-name">{client.name}</span>
            <div className="athlete-email-wrapper">
              <a className="athlete-email" href={`mailto:${client.email}`}>
                {client.email}
              </a>
              <button
                type="button"
                className="edit-email-btn"
                onClick={(e) => {
                  e.preventDefault();
                  onEditEmail();
                }}
                title={t("accessRequests.editEmailButtonTitle")}
                data-icon-only
              >
                <SvgIcon src="/img/new-icons/edit-pencil.svg" />
              </button>
            </div>
          </div>
        </div>
      </td>
      <td>{moment(client.addedAt).format("MMM DD, YYYY")}</td>
      <td>
        <div className="action-cell">
          <button
            data-button-type="outline"
            onClick={handleAccept}
            type="button"
            data-icon
          >
            <SvgIcon src="/img/svg/thumbs-up.svg" noFilterOnLight />
            <span>{t("accessRequests.acceptButton")}</span>
          </button>
          <button
            data-button-type="secondary"
            data-icon-only
            onClick={handleReject}
            type="button"
          >
            <SvgIcon src="/img/new-icons/red-cross.svg" noFilterOnLight />
          </button>
          {isActivePullCoach && (
            <div className="menu-wrapper">
              <button
                data-button-type="secondary"
                data-icon-only
                onClick={() => setMenuOpen(!menuOpen)}
                type="button"
              >
                <SvgIcon src="/img/new-icons/vuesax-linear-add-square.svg" />
              </button>
              <DropdownOptions
                options={dropdownOptions}
                onSelectFn={handleDropdownSelect}
                opened={menuOpen}
                initialDropwdownState={false}
                setDropdownState={setMenuOpen}
              />
            </div>
          )}
        </div>
      </td>
    </tr>
  );
};

const EmptyState = () => {
  const { t } = useTranslation("coaching-portal");
  return (
    <tbody>
      <tr>
        <td colSpan="100%">
          <div className="empty-state">
            <p>{t("accessRequests.emptyStateTitle")}</p>
            <span>{t("accessRequests.emptyStateDescription")}</span>
          </div>
        </td>
      </tr>
    </tbody>
  );
};

export default AccessRequestsTable;
